// packages/web/src/context/SubscriptionContext.tsx
// ═══════════════════════════════════════════════════════════════════
// Subscription Context — loads org's plan, seats, and feature flags
// once on auth, caches, and exposes `hasFeature(key)` across the UI.
// ═══════════════════════════════════════════════════════════════════

import React, {
  createContext, useContext, useEffect, useState, useCallback,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { getUsageDashboard, type UsageDashboard } from '../services/subscription.api';

interface SubscriptionContextValue {
  /** Full usage dashboard (null while loading) */
  usage: UsageDashboard | null;
  /** Quick feature-flag check: hasFeature('payroll') */
  hasFeature: (moduleKey: string) => boolean;
  /** Current tier (e.g. 'starter', 'professional', 'enterprise') */
  tier: string;
  /** Subscription status (e.g. 'active', 'trialing') */
  status: string;
  /** How many seats are used / max */
  seats: { used: number; max: number; remaining: number };
  /** Is data still loading? */
  isLoading: boolean;
  /** Force-refresh subscription data */
  refresh: () => Promise<void>;
}

const defaults: SubscriptionContextValue = {
  usage: null,
  hasFeature: () => true, // permissive default while loading
  tier: 'standard',
  status: 'active',
  seats: { used: 0, max: 0, remaining: 0 },
  isLoading: true,
  refresh: async () => {},
};

const SubscriptionContext = createContext<SubscriptionContextValue>(defaults);

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth();
  const [usage, setUsage] = useState<UsageDashboard | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const load = useCallback(async () => {
    if (!isAuthenticated || !user) {
      setUsage(null);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const res = await getUsageDashboard();
      if (res.success && res.data) {
        setUsage(res.data);
      }
    } catch (err) {
      console.warn('[SubscriptionContext] Failed to load usage:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => { load(); }, [load]);

  const hasFeature = useCallback(
    (moduleKey: string): boolean => {
      // Super admins / platform admins bypass all feature gates
      const roleName = user?.role?.name?.toLowerCase() ?? '';
      if (user?.is_platform_admin || ['super_admin', 'platform_super_admin', 'tenant_owner', 'tenant_admin'].includes(roleName)) {
        return true;
      }
      // While loading or if no data, be permissive (don't lock out mid-load)
      if (!usage) return true;
      const mods = usage.features ?? {};
      // If no feature flags at all (e.g., legacy plan), allow everything
      if (Object.keys(mods).length === 0) return true;
      return !!mods[moduleKey];
    },
    [usage, user],
  );

  const value: SubscriptionContextValue = {
    usage,
    hasFeature,
    tier: usage?.tier ?? user?.organization?.subscription_tier ?? 'standard',
    status: usage?.subscription_status ?? 'active',
    seats: usage?.seats ?? { used: 0, max: 0, remaining: 0 },
    isLoading,
    refresh: load,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription(): SubscriptionContextValue {
  return useContext(SubscriptionContext);
}
